import { orderBy } from 'lodash';
import { Moment } from 'moment';
import { reactive } from 'vue';
import ContactContext from '@/contact/models/ContactContext';
import WorkStatus from '@/dossier/constants/WorkStatus';
import { DossierId } from '@/dossier/models/AbstractDossier';
import Dossier from '@/dossier/models/Dossier';
import DmsService from '@/dossier/services/DmsService';
import DmsClient from '@/general/client/DmsClient';
import AuthService from '@/general/services/AuthService';
import { ListCache } from '@/general/utils/cache';
import Tag from '@/shared/models/Tag';
import User, { UserId } from '@/shared/models/User';
import serviceEvents, {
  DiagnosticCreated,
  DiagnosticRemoved,
  DiagnosticUpdated,
  DmsImportFinished,
  DossierWorkStatusChanged,
  JobRemoved,
  JobStatusUpdated,
  JobTagsUpdated,
  JobUpdated,
  MediaUpdated,
  UserLoggedOut,
} from '@/shared/services/events/serviceEvents';
import DossierContext from '@/dossier/models/DossierContext';
import FluidService from '@/dossier/services/FluidService';
import pushService from '@/general/services/PushService';
import ReferenceService from '@/general/services/ReferenceService';
import TagType from '@/shared/constants/TagType';
import { mergeInto, removeById } from '@/shared/utils';
import { withRawResults } from '@/shared/utils/component';
import { isPresent, present } from '@/shared/utils/typescript';
import Vehicle from '@/vehicle/models/Vehicle';
import DiagnosticClient from '../client/DiagnosticClient';
import JobClient from '../client/JobClient';
import DiagnosticType from '../constants/DiagnosticType';
import JobSectionGroup from '../constants/JobSectionGroup';
import OrderStatus from '../constants/OrderStatus';
import PriceApprovalStatus from '../constants/PriceApprovalStatus';
import { parseJobMaster } from '../converters/jobMasterConverters';
import { JobId } from '../models/AbstractJob';
import Job, { JobApprovalData, JobDescriptionData, JobOrderApprovalData, JobPriceApprovalData, JobPriceData, UpdateExecutionStatusData } from '../models/Job';
import JobContext from '../models/JobContext';
import JobMaster from '../models/JobMaster';
import PauseJobData from '../models/PauseJobData';
import Diagnostic from '../models/diagnosis/Diagnostic';
import DiagnosticService from './DiagnosticService';

class JobService {
  private pauseReasons = new ListCache(withRawResults(JobClient.loadPauseReasons));

  constructor() {
    serviceEvents.on(UserLoggedOut, () => this.pauseReasons.clear());
  }

  async loadAll(dossier: Dossier, vehicle: Vehicle, contactContext: ContactContext): Promise<JobContext> {
    const context = reactive(new JobContext(dossier, vehicle, contactContext)) as JobContext;
    this.startGeneralListeners(context);
    context.jobs = await this.loadActiveJobs(dossier.id);
    return context;
  }

  private async loadActiveJobs(dossierId: DossierId): Promise<Array<Job>> {
    const jobs = await JobClient.loadAll(dossierId);
    const activeJobs = jobs.filter(job => !job.isDeleted);
    return orderBy(activeJobs, 'sequenceNumber');
  }

  async loadMaster(dossierContext: DossierContext): Promise<JobContext> {
    const dossier = dossierContext.dossier;

    const context = reactive(dossierContext.jobContext.clone(true)) as JobContext;
    this.startMasterListeners(context);

    [context.summaries, dossierContext.fluids, context.diagnosticTypes] = await Promise.all([
      JobClient.loadMaster(dossier.id),
      FluidService.loadReplenished(dossier),
      DiagnosticClient.loadDiagnosticTypes(dossier.id),
      ReferenceService.loadTags(TagType.JOB, dossier.outletId),
    ]);

    return context;
  }

  async loadDetail(jobId: JobId | null, context: JobContext): Promise<void> {
    if (isPresent(jobId)) {
      const existingJob = context.jobs.find(j => j.id === jobId);      
      const detailedJob = await JobClient.loadDetail(jobId, context.dossier.id);

      // Preserve UI metadata from the existing job
      if (existingJob) {
        detailedJob.tags = existingJob.tags;

        if (!detailedJob.imageCount && existingJob.imageCount) {
          detailedJob.imageCount = existingJob.imageCount;
        }
        if (!detailedJob.videoCount && existingJob.videoCount) {
          detailedJob.videoCount = existingJob.videoCount;
        }
        if (!detailedJob.attachmentCount && existingJob.attachmentCount) {
          detailedJob.attachmentCount = existingJob.attachmentCount;
        }
        if (!detailedJob.diagnosticCount && existingJob.diagnosticCount) {
          detailedJob.diagnosticCount = existingJob.diagnosticCount;
        }
        if (!detailedJob.comment && existingJob.comment) {
          detailedJob.comment = existingJob.comment;
        }
      }
      
      context.selectedJob = detailedJob;
      mergeInto(detailedJob, context.jobs);
    } else {
      context.selectedJob = null;
    }
  }

  async loadPauseReasons(): Promise<void> {
    await this.pauseReasons.get();
  }

  getPauseReasons(): Array<Tag> {
    return this.pauseReasons.getCached();
  }

  startGeneralListeners(context: JobContext, originalContext?: JobContext): void {
    context.freeHooks.push(
      serviceEvents.on(JobUpdated, e => this.handleJobUpdate(e.dossierId, e.jobId, context, originalContext)),
      serviceEvents.on(JobStatusUpdated, e => this.handleJobStatusUpdated(e.dossierId, e.job, context)),
      serviceEvents.on(DmsImportFinished, e => this.handleDmsImportFinished(e.dossierId, context)),
      serviceEvents.on(DossierWorkStatusChanged, e => this.handleWorkStatusUpdate(context, e.dossierId, e.workStatus, e.jobs)),
      serviceEvents.on(DiagnosticCreated, e => this.updateDiagnosticCount(e, context)),
      serviceEvents.on(DiagnosticUpdated, e => this.handleDiagnosticUpdated(e, context)),
      serviceEvents.on(DiagnosticRemoved, e => this.handleDiagnosticRemoved(e, context)),
      serviceEvents.on(MediaUpdated, e => this.handleMediaUpdate(e, context)),
    );
  }

  private startMasterListeners(context: JobContext): void {
    this.startGeneralListeners(context);
    const dossierId = context.dossier.id;
    context.freeHooks.push(
      pushService.bind(`dossiers-${dossierId}/jobs-master`, j => context.addSummary(present(parseJobMaster(j)))),
    );
  }

  async handleJobUpdate(dossierId: DossierId, jobId: JobId, context: JobContext, originalContext?: JobContext): Promise<void> {
    if (dossierId !== context?.dossier.id) return;
    if (!context) return;
    const job = await JobClient.loadDetail(jobId, context.dossier.id);
    if (job.isDeleted) {
      removeById(context.jobs, job.id);
    } else {
      if (jobId === context.selectedJob?.id) context.selectedJob = job;
      mergeInto(job, context.jobs);
    }

    if (originalContext) context.initOriginals(originalContext);
  }

  async handleJobStatusUpdated(dossierId: DossierId, job: Job, context: JobContext): Promise<void> {
    if (dossierId !== context?.dossier.id) return;
    if (!context) return;
    const savedJob = context.jobs.find(j => j.id === job.id);
    if (!savedJob) return;
    savedJob.executionStatus = job.executionStatus;
    savedJob.priceApproval = job.priceApproval;
    savedJob.order = job.order;
  }

  private async handleDmsImportFinished(dossierId: DossierId, context: JobContext): Promise<void> {
    if (context.dossier.id !== dossierId) return;

    [context.summaries, context.jobs] = await Promise.all([
      JobClient.loadMaster(dossierId),
      this.loadActiveJobs(dossierId),
    ]);

    if (context.selectedJob) {
      try {
        context.selectedJob = await JobClient.loadDetail(context.selectedJob.id, context.dossier.id);
        mergeInto(context.selectedJob, context.jobs);
      } catch (e) {
        await serviceEvents.fire(new JobRemoved(context.selectedJob.id));
      }
    }
  }

  private async handleWorkStatusUpdate(context: JobContext | null, dossierId: DossierId, to: WorkStatus, jobs: Array<Job> = []): Promise<void> {
    if (context?.dossier.id === dossierId) {
      const sanitizedJobs = to !== WorkStatus.BEING_HANDLED && jobs.length === 0 ? context.jobs : jobs;
      this.getWorkStatusUpdateJobs(context, to, sanitizedJobs)
        .filter(j => j.getSectionGroup() === JobSectionGroup.NORMAL)
        .forEach(j => j.workStatus = to);
    }
  }

  private updateDiagnosticCount(event: DiagnosticCreated | DiagnosticRemoved, context: JobContext): void {
    const item = context.summaries.find(s => s.id === event.job.id);
    if (!item) return;
    const count = event instanceof DiagnosticRemoved ? -1 : 1;
    if (event.diagnostic.type === DiagnosticType.COMMENT) {
      item.commentCount += count;
    } else {
      item.diagnosticCount += count;
    }
  }

  private async handleDiagnosticUpdated(e: DiagnosticCreated, context: JobContext): Promise<void> {
    if (e.diagnostic.type !== DiagnosticType.COMPLAINT) return;
    const job = context.jobs.find(j => j.id === e.job.id);
    if (!job) return;
    mergeInto(e.diagnostic, job.diagnostics);
  }

  private async handleDiagnosticRemoved(e: DiagnosticRemoved, context: JobContext): Promise<void> {
    this.updateDiagnosticCount(e, context);
    if (e.diagnostic.type !== DiagnosticType.COMPLAINT) return;
    const job = context.jobs.find(j => j.id === e.job.id);
    if (!job) return;
    removeById(job.diagnostics, e.diagnostic.id);
    this.recalculatePrimaryDiagnosticComplaint(job, e.job.diagnostics);
  }

  private recalculatePrimaryDiagnosticComplaint(job: Job, diagnostics: Array<Diagnostic>): void {
    const newPrimary = diagnostics.find(d => d.type === DiagnosticType.COMPLAINT);
    if (!newPrimary) return;
    newPrimary.isPrimary = true;
    mergeInto(newPrimary, job.diagnostics);
  }

  private handleMediaUpdate(event: MediaUpdated, context: JobContext): void {
    const jobSummary = context.getSummaryByJobId(event.targetData.entityId as JobId);
    if (jobSummary) jobSummary.mediaCount = event.mediaCount;
  }

  private getWorkStatusUpdateJobs(context: JobContext, to: WorkStatus, jobs: Array<Job>): Array<Job> {
    const outletId = context.dossier.outletId;
    const outlet = AuthService.getCurrentUser().getOutlet(outletId);

    switch (to) {
      case WorkStatus.BEING_HANDLED:
        return outlet.hasContinuousTimeRegistration ? context.jobs.filter(j => !j.isReady()) : jobs;
      case WorkStatus.ON_HOLD:
      case WorkStatus.WORK_READY:
        return jobs.filter(job => job.workStatus === WorkStatus.BEING_HANDLED);
      default:
        return [];
    }
  }

  async updateOrderApproval(job: Job, data: JobOrderApprovalData, context: JobContext): Promise<void> {
    if (data.approvalStatus === job.order.status) return;
    if (data.approvalStatus === OrderStatus.NOT_GIVEN || data.approvalStatus === OrderStatus.INVISIBLE) data.approvalBy = null;
    const clone = job.clone();
    clone.order.update(data);
    await JobClient.saveOrderApprovals([clone], data, context.dossier.id);
    job.order = clone.order;
  }

  async updatePriceApproval(job: Job, context: JobContext, data: JobPriceApprovalData): Promise<void> {
    if (data.approvalStatus === job.priceApproval.status) return;
    if (data.approvalStatus === PriceApprovalStatus.NOT_GIVEN) data.approvalBy = null;
    const clone = job.clone();
    clone.priceApproval.update(data);
    await JobClient.savePriceApprovals([clone], data, context.dossier.id);
    job.priceApproval = clone.priceApproval;
  }

  async saveApprovals(jobs: Array<Job>, data: JobApprovalData, context: JobContext): Promise<void> {
    const priceChangedJobs = jobs.filter(job => job.hasPriceChanges());
    const priceApprovalChangedJobs = jobs.filter(job => job.priceApproval.status !== job.original?.priceApproval.status);
    const orderApprovalChangedJobs = jobs.filter(job => job.order.status !== job.original?.order.status);

    const warrantyStatuses = await Promise.all(priceChangedJobs.map(job => JobClient.updatePrice(context.dossier.id, job)));
    context.updatePrices(priceChangedJobs, warrantyStatuses);

    if (priceApprovalChangedJobs.length > 0) {
      await JobClient.savePriceApprovals(priceApprovalChangedJobs, data, context.dossier.id);
      context.updatePriceApprovals(priceApprovalChangedJobs, data);
    }
    if (orderApprovalChangedJobs.length > 0) {
      await JobClient.saveOrderApprovals(orderApprovalChangedJobs, data, context.dossier.id);
      context.updateOrderApprovals(orderApprovalChangedJobs, data);
    }
  }

  async updatePrice(job: Job, data: JobPriceData, context: JobContext): Promise<void> {
    const warrantyStatus = await JobClient.updatePrice(context.dossier.id, job.clone().updatePrice(data));
    job.updatePrice(data);
    job.warrantyStatus = warrantyStatus;
  }

  async toggleNeedsFollowUp(job: Job, dossierId: DossierId): Promise<void> {
    const newValue = !job.needFollowUp;
    await JobClient.updateNeedsFollowUp(job.id, newValue, dossierId);
    job.needFollowUp = newValue;
  }

  async loadDiagnosis(job: Job, context: JobContext): Promise<void> {
    [context.diagnosticTypes] = await Promise.all([
      DiagnosticClient.loadDiagnosticTypes(context.dossier.id),
      ReferenceService.loadTags(TagType.JOB, context.dossier.outletId),
      DiagnosticService.loadByJob(job, context.dossier.id),
      this.loadMedia(job, context.dossier.id),
    ]);
  }

  async loadMedia(job: Job, dossierId: DossierId): Promise<void> {
    if (job.hasMedia()) job.mediaFiles = await JobClient.loadMedia(present(job.id), dossierId);
  }

  async updateExecutionStatus(data: UpdateExecutionStatusData, isAdmin: boolean, context: JobContext): Promise<void> {
    const newExecutionStatus = await JobClient.updateExecutionStatus(data, isAdmin, context.dossier.id);
    data.jobs.forEach(j => {
      j.executionStatus = newExecutionStatus;
      const summary = context.summaries.find(s => s.id === j.id);
      if (summary) summary.executionStatus = newExecutionStatus;
    });
  }

  async updateTags(job: Job, tags: Array<Tag>, context: JobContext): Promise<void> {
    const updatedTags = await JobClient.updateTags(present(job.id), tags, context.dossier.id);
    job.tags = updatedTags;
    const summary = context.summaries.find(s => s.id === job.id);
    if (summary) summary.tags = updatedTags;
    await serviceEvents.fire(new JobTagsUpdated(job));
  }

  async assign(targetId: UserId, job: Job, dossierId: DossierId): Promise<void> {
    const newTargetId = job.assignedTo?.id === targetId ? null : targetId;
    await JobClient.assign(newTargetId, present(job.id), dossierId);
    job.assignedTo = User.withId(targetId);
  }

  async updateWorkStatus(job: Job, workStatus: WorkStatus, context: JobContext): Promise<void> {
    await JobClient.updateWorkStatus(workStatus, job.id, context.dossier.id);
    job.workStatus = workStatus;
  }

  async pause(data: PauseJobData, job: Job, dossierId: DossierId): Promise<void> {
    await JobClient.pause(data, present(job.id), dossierId);

    job.workStatus = WorkStatus.ON_HOLD;
    job.pauseReason = data.pauseReason;
    job.needFollowUp = data.needFollowUp;
  }

  async importDmsQuotes(context: JobContext): Promise<void> {
    const dmsStatus = await DmsClient.importQuotes(context.dossier.id);
    await DmsService.processSyncWithStatus(dmsStatus);
  }

  async updateDescription(job: Job, data: JobDescriptionData, dossierId: DossierId): Promise<void> {
    await JobClient.updateDescription(data, job.id, dossierId);
    job.description.content = data.description;
    job.severity = data.severity;
  }

  async updateAllowedDiagnosticTime(job: Job, time: Moment | null, dossierId: DossierId): Promise<void> {
    job.allowedDiagnosticDuration = await JobClient.updateAllowedDiagnosticTime(job.id, time, dossierId);
  }

  async remove(id: JobId, context: JobContext): Promise<void> {
    await JobClient.remove(id, context.dossier.id);
    removeById(context.jobs, id);
    removeById(context.summaries, id);
    await serviceEvents.fire(new JobRemoved(id));
  }

  async linkComment(comment: JobMaster, job: JobMaster | null, context: JobContext): Promise<void> {
    await JobClient.linkComment(comment.id, job?.id ?? null, context.dossier.id);
    comment.linkedJob = job;
  }
}

export default new JobService();